import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { Portal } from '../components/Portal';
import { useSettings } from '../hooks/useSettings';
import { useTenant } from '../contexts/TenantContext';

// Componente para exibir contador do QR Code
function QRCountdown({ expiresAt }: { expiresAt: Date }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const difference = expiry - now;
      return Math.max(0, Math.ceil(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft <= 0) {
    return <span className="text-red-600 text-sm">QR Code expirado</span>;
  }

  return (
    <span className="text-orange-600 text-sm">
      Expira em {timeLeft}s
    </span>
  );
}

interface WhatsAppSession {
  name: string; // Nome real usado na API (ex: vendas_c52982e8)
  displayName?: string; // Nome exibido ao usuário (ex: vendas)
  status: 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
  provider: 'WAHA' | 'EVOLUTION' | 'QUEPASA';
  qr?: string;
  qrExpiresAt?: Date;
  me?: {
    id: string;
    pushName: string;
  };
  interactiveCampaignEnabled?: boolean; // Bot/Campanha Interativa habilitado
}


export function WhatsAppConnectionsPage() {
  const { settings } = useSettings();
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionProvider, setNewSessionProvider] = useState<'WAHA' | 'EVOLUTION' | 'QUEPASA'>('WAHA');
  const [interactiveCampaignEnabled, setInteractiveCampaignEnabled] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingQR, setLoadingQR] = useState<string | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQRSession, setCurrentQRSession] = useState<WhatsAppSession | null>(null);
  const [createSessionModalOpen, setCreateSessionModalOpen] = useState(false);
  const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
  const [allowedProviders, setAllowedProviders] = useState<string[]>(['WAHA', 'EVOLUTION', 'QUEPASA']);
  const [sessionNameError, setSessionNameError] = useState<string>('');

  // Função para validar nome da sessão
  const validateSessionName = (name: string): { isValid: boolean; error: string; sanitized: string } => {
    // Remover espaços e converter para minúsculas
    const sanitized = name.trim().toLowerCase().replace(/\s+/g, '-');

    // Validar formato: apenas letras minúsculas, números e hífen
    const validFormat = /^[a-z0-9-]+$/;

    if (!sanitized) {
      return { isValid: false, error: '', sanitized: '' };
    }

    if (!validFormat.test(sanitized)) {
      return {
        isValid: false,
        error: 'Use apenas letras, números e hífen (-)',
        sanitized
      };
    }

    if (sanitized.length < 3) {
      return {
        isValid: false,
        error: 'Mínimo de 3 caracteres',
        sanitized
      };
    }

    if (sanitized.length > 30) {
      return {
        isValid: false,
        error: 'Máximo de 30 caracteres',
        sanitized
      };
    }

    return { isValid: true, error: '', sanitized };
  };

  // Handler para mudança do nome da sessão com conversão automática
  const handleSessionNameChange = (value: string) => {
    // Converter automaticamente para formato válido enquanto digita
    const sanitized = value
      .toLowerCase()                    // Converter para minúsculas
      .replace(/\s+/g, '-')            // Substituir espaços por hífen
      .replace(/[^a-z0-9-]/g, '');     // Remover caracteres especiais

    setNewSessionName(sanitized);

    if (sanitized) {
      const validation = validateSessionName(sanitized);
      setSessionNameError(validation.error);
    } else {
      setSessionNameError('');
    }
  };

  // Preload das imagens dos provedores para carregamento instantâneo
  useEffect(() => {
    const images = [
      '/iconewaha.png',
      '/iconeevolutionapi.png',
      '/iconequepasa.png'
    ];

    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Carregar provedores permitidos do tenant
  const loadAllowedProviders = async () => {
    try {
      const response = await authenticatedFetch('/api/tenants/allowed-providers');
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.allowedProviders)) {
          setAllowedProviders(data.allowedProviders);
          // Definir o primeiro provedor permitido como padrão
          if (data.allowedProviders.length > 0 && !data.allowedProviders.includes(newSessionProvider)) {
            setNewSessionProvider(data.allowedProviders[0] as 'WAHA' | 'EVOLUTION' | 'QUEPASA');
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar provedores permitidos:', error);
    }
  };

  useEffect(() => {
    // Não carregar sessões até que o tenant esteja definido
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    // Carregar provedores permitidos
    loadAllowedProviders();

    // Recarregar sessões quando o tenant mudar
    loadSessions();

    // Iniciar polling a cada 5 segundos
    const interval = setInterval(() => {
      loadSessions(false); // Não mostrar loading durante polling automático
    }, 5000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedTenantId, tenantLoading]); // Recarrega quando o tenant mudar ou terminar de carregar

  // Polling para verificar se a conexão foi estabelecida quando o modal QR está aberto
  useEffect(() => {
    if (!qrModalOpen || !currentQRSession) {
      return;
    }

    const checkConnection = async () => {
      try {
        const response = await authenticatedFetch('/api/waha/sessions');
        if (response.ok) {
          const sessions = await response.json();
          const updatedSession = sessions.find((s: any) => s.name === currentQRSession.name);

          if (updatedSession) {
            console.log(`🔍 Polling - sessão ${currentQRSession.name}:`, {
              provider: updatedSession.provider,
              status: updatedSession.status,
              me: updatedSession.me
            });

            // Se a sessão mudou para WORKING (conectada)
            if (updatedSession.status === 'WORKING') {
              console.log(`✅ Sessão ${currentQRSession.name} conectada! Fechando modal...`);

              // Fechar modal
              setQrModalOpen(false);
              setCurrentQRSession(null);

              // Atualizar lista de sessões imediatamente
              await loadSessions(false);

              // Mostrar notificação de sucesso
              const userName = updatedSession.me?.pushName || 'Usuário';
              toast.success(`WhatsApp conectado com sucesso! Logado como: ${userName}`);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status da conexão:', error);
      }
    };

    // Verificar imediatamente
    checkConnection();

    // Depois verificar a cada 2 segundos (polling mais frequente)
    const interval = setInterval(checkConnection, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [qrModalOpen, currentQRSession]);

  const loadSessions = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await authenticatedFetch('/api/waha/sessions');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Processar dados das sessões incluindo QR code salvo no banco
      const processedSessions = data.map((session: any) => ({
        name: session.name,
        displayName: session.displayName || session.name,
        status: session.status || 'STOPPED',
        provider: session.provider || 'WAHA',
        me: session.me || null,
        qr: session.qr || null,
        qrExpiresAt: session.qrExpiresAt ? new Date(session.qrExpiresAt) : undefined,
        interactiveCampaignEnabled: session.interactiveCampaignEnabled || false
      }));

      // Ordenação estável: manter ordem existente se a sessão já existe, adicionar novas no final
      setSessions(prevSessions => {
        // Criar mapa de índices das sessões existentes para manter a ordem
        const existingOrder = new Map(prevSessions.map((s, idx) => [s.name, idx]));

        // Separar sessões existentes (atualizar apenas dados) e novas
        const existing: typeof processedSessions = [];
        const newSessions: typeof processedSessions = [];

        processedSessions.forEach(session => {
          if (existingOrder.has(session.name)) {
            existing.push(session);
          } else {
            newSessions.push(session);
          }
        });

        // Ordenar sessões existentes pela ordem anterior
        existing.sort((a, b) => {
          const idxA = existingOrder.get(a.name) ?? 999999;
          const idxB = existingOrder.get(b.name) ?? 999999;
          return idxA - idxB;
        });

        // Ordenar novas sessões alfabeticamente
        newSessions.sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name, 'pt-BR'));

        // Retornar: sessões existentes na ordem original + novas no final
        return [...existing, ...newSessions];
      });

      if (showLoading) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      if (showLoading) {
        toast.error('Erro ao carregar sessões WhatsApp');
        setLoading(false);
      }
    }
  };

  const createSession = async () => {
    if (!newSessionName.trim()) {
      toast.error('Digite um nome para a sessão');
      return;
    }

    // Validar se as configurações do provedor estão disponíveis
    if (newSessionProvider === 'WAHA' && (!settings?.wahaHost || !settings?.wahaApiKey)) {
      toast.error('Configure as credenciais WAHA nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'EVOLUTION' && (!settings?.evolutionHost || !settings?.evolutionApiKey)) {
      toast.error('Configure as credenciais Evolution API nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'QUEPASA' && (!settings?.quepasaUrl || !settings?.quepasaLogin || !settings?.quepasaPassword)) {
      toast.error('Configure as credenciais Quepasa nas configurações do sistema');
      return;
    }

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/api/waha/sessions', {
        method: 'POST',
        body: JSON.stringify({
          name: newSessionName.trim(),
          provider: newSessionProvider,
          interactiveCampaignEnabled
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

        // Verificar se é erro de quota
        if (errorData.upgradeRequired || (errorData.message && errorData.message.includes('Limite'))) {
          toast.error(errorData.message || 'Limite de conexões atingido. Faça upgrade do seu plano para continuar.', {
            duration: 6000,
            icon: '⚠️'
          });
          throw new Error(errorData.message);
        }

        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const createdSessionData = await response.json();

      toast.success(`Sessão ${newSessionProvider} criada com sucesso`);
      setNewSessionName('');
      setNewSessionProvider('WAHA');

      // Recarregar imediatamente
      await loadSessions();

      // Se for Evolution e veio QR code, abrir modal automaticamente
      if (newSessionProvider === 'EVOLUTION' && createdSessionData.qrcode?.base64) {
        setTimeout(async () => {
          // Buscar o nome real da sessão criada
          const sessions = await authenticatedFetch('/api/waha/sessions').then(r => r.json());
          const createdSession = sessions.find((s: any) => s.name.startsWith(newSessionName.trim()));

          if (createdSession) {
            await openQRModal(createdSession.name);
          }
        }, 1000);
      }

      // Aguardar mais um pouco e recarregar novamente para pegar status atualizado
      setTimeout(() => {
        loadSessions(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao criar sessão:', error);
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error instanceof Error && error.message.includes('Limite'))) {
        toast.error(error instanceof Error ? error.message : 'Erro ao criar sessão');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const deleteSession = async (sessionName: string) => {
    if (!confirm(`Tem certeza que deseja remover a sessão "${sessionName}"?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão removida com sucesso');
      loadSessions();
    } catch (error) {
      console.error('Erro ao remover sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao remover sessão');
    }
  };

  const restartSession = async (sessionName: string) => {
    if (qrModalOpen && currentQRSession?.name === sessionName) {
      closeQRModal();
    }

    const doRestart = async () => {
      const refreshResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/qr/refresh`, {
        method: 'POST'
      });

      if (!refreshResponse.ok) {
        const errorData = await refreshResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${refreshResponse.status}`);
      }

      const refreshData = await refreshResponse.json();
      await loadSessions(false);

      // Usar QR da resposta do refresh se disponível; senão buscar da lista de sessões
      const qrString = typeof refreshData?.qr === 'string'
        ? refreshData.qr
        : refreshData?.qr?.base64
          ? (refreshData.qr.base64.startsWith('data:image') ? refreshData.qr.base64 : `data:image/png;base64,${refreshData.qr.base64}`)
          : refreshData?.qrcode?.base64
            ? (refreshData.qrcode.base64.startsWith('data:image') ? refreshData.qrcode.base64 : `data:image/png;base64,${refreshData.qrcode.base64}`)
            : null;
      const qrExpiresAt = refreshData?.qrExpiresAt ? new Date(refreshData.qrExpiresAt) : null;

      if (qrString) {
        setCurrentQRSession({
          name: sessionName,
          status: 'SCAN_QR_CODE',
          provider: (refreshData.provider as 'WAHA' | 'EVOLUTION' | 'QUEPASA') || 'WAHA',
          qr: qrString,
          qrExpiresAt: qrExpiresAt || new Date(Date.now() + 300000),
          me: undefined
        });
        setQrModalOpen(true);
      } else {
        const sessionsResponse = await authenticatedFetch('/api/waha/sessions');
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          const updatedSession = sessionsData.find((s: any) => s.name === sessionName);
          if (updatedSession?.qr && updatedSession?.qrExpiresAt) {
            setCurrentQRSession({
              name: updatedSession.name,
              status: 'SCAN_QR_CODE',
              provider: updatedSession.provider,
              qr: updatedSession.qr,
              qrExpiresAt: new Date(updatedSession.qrExpiresAt),
              me: updatedSession.me
            });
            setQrModalOpen(true);
          }
        }
      }
    };

    toast.promise(doRestart(), {
      loading: 'Reiniciando sessão e gerando novo QR...',
      success: 'Sessão reiniciada! Novo QR gerado!',
      error: (err) => err instanceof Error ? err.message : 'Erro ao reiniciar sessão'
    }).catch(() => {});
  };

  const requestQRCode = async (sessionName: string) => {
    setLoadingQR(sessionName);
    try {
      // Primeiro, iniciar a sessão
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${startResponse.status}`);
      }

      toast.success('QR Code solicitado. Aguarde...');

      // Aguardar um pouco e abrir modal para mostrar QR
      setTimeout(async () => {
        await openQRModal(sessionName);
        loadSessions(false);
      }, 2000);

    } catch (error) {
      console.error('Erro ao solicitar QR Code:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao solicitar QR Code');
    } finally {
      setLoadingQR(null);
    }
  };

  const openQRModal = async (sessionName: string, forceRefresh: boolean = false) => {
    try {
      const currentSession = sessions.find(s => s.name === sessionName);

      // Se forceRefresh=true, chamar endpoint de refresh dedicado
      if (forceRefresh) {
        console.log(`🔄 Forçando refresh do QR Code para ${sessionName}...`);

        const refreshResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/qr/refresh`, {
          method: 'POST'
        });

        if (!refreshResponse.ok) {
          const errorData = await refreshResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
          toast.error(`Erro ao atualizar QR: ${errorData.error}`);
          return;
        }

        const refreshData = await refreshResponse.json();
        await loadSessions(false);

        // Usar QR da resposta do refresh; fallback para lista de sessões
        const qrString = typeof refreshData?.qr === 'string'
          ? refreshData.qr
          : refreshData?.qr?.base64
            ? (refreshData.qr.base64.startsWith('data:image') ? refreshData.qr.base64 : `data:image/png;base64,${refreshData.qr.base64}`)
            : refreshData?.qrcode?.base64
              ? (refreshData.qrcode.base64.startsWith('data:image') ? refreshData.qrcode.base64 : `data:image/png;base64,${refreshData.qrcode.base64}`)
              : null;
        const qrExpiresAt = refreshData?.qrExpiresAt ? new Date(refreshData.qrExpiresAt) : undefined;

        if (qrString) {
          setCurrentQRSession({
            name: sessionName,
            status: 'SCAN_QR_CODE',
            provider: (refreshData.provider as 'WAHA' | 'EVOLUTION' | 'QUEPASA') || currentSession?.provider || 'WAHA',
            qr: qrString,
            qrExpiresAt: qrExpiresAt || new Date(Date.now() + 300000),
            me: undefined
          });
          setQrModalOpen(true);
          toast.success('QR Code atualizado!');
        } else {
          // Fallback 1: lista de sessões
          const sessionsResponse = await authenticatedFetch('/api/waha/sessions');
          if (sessionsResponse.ok) {
            const sessionsData = await sessionsResponse.json();
            const updatedSession = sessionsData.find((s: any) => s.name === sessionName);
            if (updatedSession?.qr) {
              setCurrentQRSession({
                name: sessionName,
                status: 'SCAN_QR_CODE',
                provider: updatedSession.provider,
                qr: updatedSession.qr,
                qrExpiresAt: updatedSession.qrExpiresAt ? new Date(updatedSession.qrExpiresAt) : undefined,
                me: updatedSession.me
              });
              setQrModalOpen(true);
              toast.success('QR Code atualizado!');
              return;
            }
          }
          // Fallback 2: GET /auth/qr (pode ter QR em cache no backend)
          const qrResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/auth/qr`);
          if (qrResponse.ok) {
            const qrData = await qrResponse.json();
            const qrFromAuth = typeof qrData.qr === 'string'
              ? qrData.qr
              : qrData.qr?.base64
                ? (qrData.qr.base64.startsWith('data:image') ? qrData.qr.base64 : `data:image/png;base64,${qrData.qr.base64}`)
                : qrData.qr?.qrcode?.base64
                  ? (qrData.qr.qrcode.base64.startsWith('data:image') ? qrData.qr.qrcode.base64 : `data:image/png;base64,${qrData.qr.qrcode.base64}`)
                  : null;
            if (qrFromAuth) {
              setCurrentQRSession({
                name: sessionName,
                status: 'SCAN_QR_CODE',
                provider: currentSession?.provider || 'WAHA',
                qr: qrFromAuth,
                qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 300000),
                me: undefined
              });
              setQrModalOpen(true);
              toast.success('QR Code atualizado!');
              return;
            }
          }
          toast.error('QR Code não disponível após refresh');
        }
        return;
      }

      // Se tem QR válido salvo, usar ele
      if (currentSession?.qr && currentSession?.qrExpiresAt && currentSession.qrExpiresAt > new Date()) {
        setCurrentQRSession({
          name: sessionName,
          status: 'SCAN_QR_CODE',
          provider: currentSession.provider,
          qr: currentSession.qr,
          qrExpiresAt: currentSession.qrExpiresAt,
          me: currentSession.me
        });
        setQrModalOpen(true);
        return;
      }

      // Se não tem QR válido, buscar através do /start
      console.log(`🔄 Buscando QR Code para ${sessionName}...`);

      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const startError = await startResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        toast.error(`Erro ao iniciar sessão: ${startError.error}`);
        return;
      }

      // Aguardar para sessão gerar QR
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Buscar QR através do endpoint de auth
      const qrResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/auth/qr`);

      if (qrResponse.ok) {
        const qrData = await qrResponse.json();

        // Normalizar QR: backend pode retornar string ou objeto { base64 } / { qrcode: { base64 } }
        const qrString = typeof qrData.qr === 'string'
          ? qrData.qr
          : qrData.qr?.base64
            ? (qrData.qr.base64.startsWith('data:image') ? qrData.qr.base64 : `data:image/png;base64,${qrData.qr.base64}`)
            : qrData.qr?.qrcode?.base64
              ? (qrData.qr.qrcode.base64.startsWith('data:image') ? qrData.qr.qrcode.base64 : `data:image/png;base64,${qrData.qr.qrcode.base64}`)
              : null;

        if (qrString) {
          setCurrentQRSession({
            name: sessionName,
            status: 'SCAN_QR_CODE',
            provider: currentSession?.provider || 'WAHA',
            qr: qrString,
            qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 60000),
            me: undefined
          });
          setQrModalOpen(true);

          // Recarregar sessões
          setTimeout(() => loadSessions(false), 1000);
        } else {
          toast.error('QR Code não disponível');
        }
      } else {
        const errorData = await qrResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        const msg = errorData?.error || errorData?.message || 'Erro ao buscar QR Code';
        toast.error(msg);
      }
    } catch (error) {
      console.error('Erro ao buscar QR Code:', error);
      toast.error('Erro ao buscar QR Code');
    }
  };

  const closeQRModal = () => {
    setQrModalOpen(false);
    setCurrentQRSession(null);
    // Recarregar sessões para verificar se conectou
    loadSessions(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'SCAN_QR_CODE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'STOPPED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'FAILED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'Conectado';
      case 'SCAN_QR_CODE':
        return 'Aguardando QR Code';
      case 'STOPPED':
        return 'Parado';
      case 'FAILED':
        return 'Erro';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando conexões...</span>
      </div>
    );
  }

  // Estatísticas das conexões do tenant (sempre baseado em sessions já filtradas por tenant)
  const connectionStats = {
    total: sessions.length,
    active: sessions.filter((s) => s.status === 'WORKING').length,
    pendingQr: sessions.filter((s) => s.status === 'SCAN_QR_CODE').length,
    stoppedOrFailed: sessions.filter((s) => s.status === 'STOPPED' || s.status === 'FAILED').length,
  };

  return (
    <>
      <Header
        title="WhatsApp"
        subtitle="Conexões do seu tenant"
        compactTopbar
      />

      <PageLayout>
        <PageHeader
          title="WhatsApp"
          breadcrumbs={[
            { label: 'Home', href: '/dashboard' },
            { label: 'WhatsApp' },
          ]}
        />
        <div className="space-y-8 w-full">
          {/* Aviso quando não há provedores configurados */}
          {allowedProviders.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800 mb-1">Nenhum provedor WhatsApp configurado</h3>
                <p className="text-sm text-yellow-700">
                  Configure as credenciais de pelo menos um provedor (WAHA, Evolution API ou Quepasa) nas <strong>Configurações do Sistema</strong> antes de criar conexões WhatsApp.
                </p>
              </div>
            </div>
          )}

          {/* Overview conexões - mesmo padrão do Dashboard */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão das conexões</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadSessions()}
                  className="btn-atualizar"
                  title="Atualizar lista de conexões"
                >
                  <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                  Atualizar
                </button>
                <button
                  type="button"
                  onClick={() => setCreateSessionModalOpen(true)}
                  disabled={allowedProviders.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={allowedProviders.length === 0 ? 'Configure um provedor WhatsApp primeiro' : ''}
                >
                  <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                  Nova conexão
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className="fas fa-phone" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Total de conexões</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{connectionStats.total}</h3>
              </div>
              <div className="border-b p-5 lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-green-600 dark:text-green-400 text-xl" aria-hidden>
                    <i className="fas fa-circle-check" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Conectadas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{connectionStats.active}</h3>
              </div>
              <div className="border-b p-5 sm:border-r sm:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden>
                    <i className="fas fa-clock" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Aguardando QR</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{connectionStats.pendingQr}</h3>
              </div>
              <div className="p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xl" aria-hidden>
                    <i className="fas fa-ban" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Paradas / Falhas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">{connectionStats.stoppedOrFailed}</h3>
              </div>
            </div>
          </div>

          {/* Tabela de conexões - mesmo padrão da tabela Últimas campanhas do Dashboard */}
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Conexões</h2>
            </div>
            <div className="overflow-x-auto">
              {sessions.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                  Nenhuma conexão encontrada. Clique em <strong>Nova conexão</strong> para começar.
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Provedor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bot</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Conectado como</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                    {sessions.map((session) => (
                      <tr key={session.name} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">
                          {session.displayName || session.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                            session.provider === 'EVOLUTION' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                            session.provider === 'QUEPASA' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          }`}>
                            <img
                              src={
                                session.provider === 'EVOLUTION' ? '/iconevolutionapi.png' :
                                session.provider === 'QUEPASA' ? '/iconequepasa.png' :
                                '/iconewaha.png'
                              }
                              alt={session.provider}
                              className="w-4 h-4 object-contain"
                            />
                            {session.provider === 'EVOLUTION' ? 'Evolution API' :
                             session.provider === 'QUEPASA' ? 'Quepasa' :
                             'Waha'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {session.interactiveCampaignEnabled ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                              </svg>
                              Ativo
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">–</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {session.me?.pushName ? (
                            <span>{session.me.pushName} <span className="text-gray-400 dark:text-gray-500">({session.me.id})</span></span>
                          ) : (
                            '–'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                            {getStatusText(session.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex items-center justify-end gap-2">
                            {session.status === 'STOPPED' && (
                              <button
                                onClick={() => requestQRCode(session.name)}
                                disabled={loadingQR === session.name}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Conectar sessão"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                {loadingQR === session.name ? 'Conectando...' : 'Conectar'}
                              </button>
                            )}
                            {session.status === 'SCAN_QR_CODE' && (
                              <button
                                onClick={() => openQRModal(session.name)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 font-medium transition-colors"
                                title="Ver QR Code"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                </svg>
                                QR Code
                              </button>
                            )}
                            <button
                              onClick={() => restartSession(session.name)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 font-medium transition-colors"
                              title="Reiniciar sessão"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Reiniciar
                            </button>
                            <button
                              onClick={() => deleteSession(session.name)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 font-medium transition-colors"
                              title="Remover sessão"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Remover
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

      {/* Modal Criar Sessão */}
      {createSessionModalOpen && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="card-padrao p-8 w-full max-w-md m-4 shadow-xl" role="dialog" aria-labelledby="create-session-title">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 id="create-session-title" className="text-2xl font-bold text-gray-900">
                Nova Sessão WhatsApp
              </h2>
              <p className="text-gray-500 mt-2">
                Digite um nome para identificar esta sessão
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="session-provider" className="block text-sm font-semibold text-gray-700 mb-2">
                  Provedor WhatsApp *
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => !isCreating && setProviderDropdownOpen(!providerDropdownOpen)}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm bg-white text-left flex items-center justify-between"
                    disabled={isCreating}
                  >
                    <span className="flex items-center gap-3">
                      <img
                        src={
                          newSessionProvider === 'EVOLUTION' ? '/iconeevolutionapi.png' :
                          newSessionProvider === 'QUEPASA' ? '/iconequepasa.png' :
                          '/iconewaha.png'
                        }
                        alt={newSessionProvider}
                        className="w-5 h-5 object-contain"
                      />
                      {newSessionProvider === 'EVOLUTION' ? 'Evolution API' :
                       newSessionProvider === 'QUEPASA' ? 'Quepasa' :
                       'Waha'}
                    </span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${providerDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {providerDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setProviderDropdownOpen(false)}
                      />
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg overflow-hidden">
                        {allowedProviders.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            Nenhum provedor configurado. Configure nas configurações do sistema.
                          </div>
                        ) : (
                          <>
                        {allowedProviders.includes('WAHA') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('WAHA');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'WAHA' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <img src="/iconewaha.png" alt="Waha" className="w-5 h-5 object-contain" />
                            <span className="text-sm">Waha</span>
                          </button>
                        )}
                        {allowedProviders.includes('EVOLUTION') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('EVOLUTION');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'EVOLUTION' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <img src="/iconeevolutionapi.png" alt="Evolution API" className="w-5 h-5 object-contain" />
                            <span className="text-sm">Evolution API</span>
                          </button>
                        )}
                        {allowedProviders.includes('QUEPASA') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('QUEPASA');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'QUEPASA' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <img src="/iconequepasa.png" alt="Quepasa" className="w-5 h-5 object-contain" />
                            <span className="text-sm">Quepasa</span>
                          </button>
                        )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Escolha o provedor para conectar ao WhatsApp
                </p>
              </div>

              <div>
                <label htmlFor="session-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome da Sessão *
                </label>
                <div className="relative">
                  <input
                    id="session-name"
                    type="text"
                    value={newSessionName}
                    onChange={(e) => handleSessionNameChange(e.target.value)}
                    placeholder="Ex: vendas, suporte, atendimento"
                    className={`w-full px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:border-transparent text-sm transition-colors ${
                      newSessionName.trim() && !sessionNameError
                        ? 'border-green-500 focus:ring-green-500 bg-green-50'
                        : sessionNameError
                        ? 'border-red-500 focus:ring-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-green-500'
                    }`}
                    disabled={isCreating}
                    autoFocus
                  />
                  {newSessionName.trim() && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {sessionNameError ? (
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
                {sessionNameError ? (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {sessionNameError}
                  </p>
                ) : newSessionName.trim() ? (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Nome válido e pronto para usar!
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">
                    Digite o nome para identificar esta sessão
                  </p>
                )}
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <label htmlFor="interactive-campaign-toggle" className="text-sm font-semibold text-gray-800 cursor-pointer">
                        Bot / Campanha Interativa
                      </label>
                    </div>
                    <p className="text-xs text-gray-600">
                      Esta conexão habilita o Bot e a Campanha Interativa (fluxos conversacionais automatizados).
                    </p>
                  </div>
                  <button
                    id="interactive-campaign-toggle"
                    type="button"
                    onClick={() => setInteractiveCampaignEnabled(!interactiveCampaignEnabled)}
                    disabled={isCreating}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      interactiveCampaignEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    } ${isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={interactiveCampaignEnabled}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        interactiveCampaignEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setCreateSessionModalOpen(false);
                    setNewSessionName('');
                    setNewSessionProvider('WAHA');
                    setInteractiveCampaignEnabled(false);
                    setSessionNameError('');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
                  disabled={isCreating}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await createSession();
                    setCreateSessionModalOpen(false);
                    setSessionNameError('');
                  }}
                  disabled={isCreating || !newSessionName.trim() || !!sessionNameError}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Criando...
                    </>
                  ) : (
                    'Criar Sessão'
                  )}
                </button>
              </div>
            </div>
          </div>
          </div>
        </Portal>
      )}

      {/* Modal do QR Code */}
      {qrModalOpen && currentQRSession && (
        <div className="qr-modal-overlay" onClick={closeQRModal}>
          <div className="qr-modal-content bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                QR Code - {currentQRSession.displayName || currentQRSession.name} ({currentQRSession.provider})
              </h3>
              <button
                onClick={closeQRModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {currentQRSession?.qr ? (
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-4">Escaneie o QR Code com seu WhatsApp</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Abra o WhatsApp no seu celular, vá em <strong>Configurações → Aparelhos conectados → Conectar aparelho</strong> e escaneie o código abaixo
                  </p>
                  <div className="bg-white p-6 rounded-lg border inline-block shadow-lg">
                    {(() => {
                      // Para Evolution: QR vem em base64 direto
                      if (currentQRSession.provider === 'EVOLUTION' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                          />
                        );
                      }

                      // Para Quepasa: QR vem em base64
                      if (currentQRSession.provider === 'QUEPASA' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                            onError={(e) => {
                              console.error('Erro ao carregar QR Quepasa base64');
                            }}
                          />
                        );
                      }

                      // Para WAHA: QR já vem processado pelo backend em base64
                      if (currentQRSession.provider === 'WAHA' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                            onError={(e) => {
                              console.error('Erro ao carregar QR WAHA base64');
                            }}
                          />
                        );
                      }

                      // Fallback: detectar automaticamente
                      const qrSrc = currentQRSession.qr?.startsWith('data:image')
                        ? currentQRSession.qr
                        : `/api/waha/sessions/${currentQRSession.name}/auth/qr`;

                      return (
                        <img
                          src={qrSrc}
                          alt="QR Code WhatsApp"
                          className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                          onError={(e) => {
                            console.error('Erro ao carregar QR fallback');
                          }}
                        />
                      );
                    })()}
                  </div>
                  {currentQRSession.qrExpiresAt && (
                    <div className="mt-4">
                      <QRCountdown expiresAt={currentQRSession.qrExpiresAt} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <span className="text-gray-600">Gerando QR Code...</span>
                  <p className="text-sm text-gray-500 mt-2">Aguarde um momento</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => openQRModal(currentQRSession.name, true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Atualizar QR
              </button>
              <button
                onClick={closeQRModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </PageLayout>
    </>
  );
}